// Hooks de dados do backoffice (fatia S5b).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminAccountDetail, AdminAccountsResponse, AdminAiRoutesResponse, AdminAiUsageResponse, AdminAuditResponse, AdminFlagsResponse,
  AdminOverrideUpdate, AdminQueuesResponse, AdminResyncResponse, AdminSiteLeadsResponse, AdminStatsResponse, AiRouteDto,
} from '@impulso/shared/api/admin'
import { api } from '../../lib/api'

export const useAdminStats = () => useQuery({ queryKey: ['admin', 'stats'], queryFn: () => api.get<AdminStatsResponse>('/admin/stats') })

export const useAdminAudit = (accountId?: string, limit = 30) =>
  useQuery({ queryKey: ['admin', 'audit', accountId, limit], queryFn: () => api.get<AdminAuditResponse>(`/admin/audit?limit=${limit}${accountId ? `&accountId=${accountId}` : ''}`) })

export const useAdminAccounts = (q: string) =>
  useQuery({ queryKey: ['admin', 'accounts', q], queryFn: () => api.get<AdminAccountsResponse>(`/admin/accounts${q ? `?q=${encodeURIComponent(q)}` : ''}`), placeholderData: (prev) => prev })

export const useAdminAccount = (id: string) =>
  useQuery({ queryKey: ['admin', 'account', id], queryFn: () => api.get<AdminAccountDetail>(`/admin/accounts/${id}`) })

export function useSetOverride(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AdminOverrideUpdate) => api.put<{ ok: true }>(`/admin/accounts/${id}/overrides`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'account', id] }),
  })
}

export const useResync = () =>
  useMutation({ mutationFn: (connectionId: string) => api.post<AdminResyncResponse>(`/admin/connections/${connectionId}/resync`) })

export const useAdminQueues = () =>
  useQuery({ queryKey: ['admin', 'queues'], queryFn: () => api.get<AdminQueuesResponse>('/admin/queues'), refetchInterval: 10_000 })

export const useAdminSiteLeads = () => useQuery({ queryKey: ['admin', 'site-leads'], queryFn: () => api.get<AdminSiteLeadsResponse>('/admin/site-leads') })

export const useAdminFlags = () => useQuery({ queryKey: ['admin', 'flags'], queryFn: () => api.get<AdminFlagsResponse>('/admin/flags') })

export function useSetFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { key: string; enabled: boolean; reason?: string }) => api.put<{ ok: true }>(`/admin/flags/${encodeURIComponent(v.key)}`, { enabled: v.enabled, reason: v.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'flags'] }),
  })
}

export const useAiUsage = (days: number) =>
  useQuery({ queryKey: ['admin', 'ai-usage', days], queryFn: () => api.get<AdminAiUsageResponse>(`/admin/ai/usage?days=${days}`) })

export const useAiRoutes = () => useQuery({ queryKey: ['admin', 'ai-routes'], queryFn: () => api.get<AdminAiRoutesResponse>('/admin/ai/routes') })

export function useSaveAiRoutes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (routes: AiRouteDto[]) => api.put<AdminAiRoutesResponse>('/admin/ai/routes', { routes }),
    onSuccess: (data) => qc.setQueryData(['admin', 'ai-routes'], data),
  })
}

export const usd = (v: number) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const dateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—')
